<template>
  <div :id="id" class="testimonials">
    <div class="componata-container">
      <div class="testimonials__badge" data-aos="fade-down">
        <inline-svg
          :src="require('@/assets/images/christmas-page/testimonials-badge.svg')"
          class="testimonials__clip"
        />

        <div>
          <inline-svg
            :src="require('@/assets/images/christmas-page/testimonials-stars.svg')"
            class="testimonials__stars"
          />

          <span class="testimonials__score">{{ content.score }}</span>

          <inline-svg
            :src="require('@/assets/images/christmas-page/testimonials-trustpilot-logo.svg')"
            class="testimonials__trustpilot-logo"
          />
        </div>
      </div>

      <FancyHeading class="testimonials__heading" data-aos="fade-down">
        <template v-slot:title><span v-html="content.title"></span></template>
        <template v-slot:description>{{ content.description }}</template>
      </FancyHeading>

      <div class="testimonials__slider" ref="slider">
        <div class="glide">
          <div class="glide__track" data-glide-el="track">
            <ul class="glide__slides">
              <li
                v-for="(item, index) in this.content.testimonials"
                :key="index"
                class="glide__slide"
              >
                <figure class="testimonials__item">
                  <div class="testimonials__card">
                    <img
                      v-if="item.logo"
                      :src="item.logo.url"
                      class="testimonials__logo"
                    >
                    <blockquote class="testimonials__quote" v-html="item.quote"></blockquote>
                  </div>

                  <div class="testimonials__author">
                    <img :src="item.author.avatar ? item.author.avatar.url : 'https://cdn.componata.de/img/default-avatar.png'" class="testimonials__author-avatar">
                    <figcaption>
                      <cite class="testimonials__author-name">{{ item.author.name }}</cite>
                      <br>
                      <span class="testimonials__author-description">{{ item.author.description }}</span>
                    </figcaption>
                  </div>
                </figure>
              </li>
            </ul>
          </div>
        </div>

        <div class="testimonials__nav --is-left" data-glide-el="controls">
          <button type="button" data-glide-dir="<"><i class="icon icon-angle-left"></i></button>
        </div>

        <div class="testimonials__nav --is-right" data-glide-el="controls">
          <button type="button" data-glide-dir=">"><i class="icon icon-angle-right"></i></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Glide from '@glidejs/glide';

import FancyHeading from '@/components/Pages/FancyHeading';

export default {
  name: 'Testimonials',
  components: {
    FancyHeading
  },
  props: {
    id: {
      type: String,
      default: 'testimonials'
    },
    content: {
      type: Object
    }
  },
  data () {
    return {
      navigationTabs: [],
      activeContentIndex: 0,
      glide: {
        instance: null,
        options: {
          type: 'slider',
          startAt: 0,
          perView: 2,
          bound: true,
          perTouch: 3,
          gap: 0,
          peek: {
            before: 0,
            after: 0
          },
          breakpoints: {
            1024: {
              perView: 2
            },
            768: {
              perView: 1
            }
          }
        }
      }
    };
  },
  methods: {
  },
  created () {
  },
  mounted () {
    this.glide.instance = new Glide(this.$refs.slider, this.glide.options);
    this.glide.instance.mount();
  }
};
</script>

<style lang="scss" scoped>
@import '~@glidejs/glide/src/assets/sass/glide.core';

/**
 * Testimonials
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Content section for showcasing Testimonials
 * @since 2022-07-05
**/

$component: 'testimonials';

.#{$component} {
  @include padding((map-get($spacers, 6) * 2) 0);
  background: $white  url('~@/assets/images/christmas-page/testimonials-bgr.svg') center top no-repeat;

  &__badge {
    position: relative;
    @include rfs(12rem, width);
    @include margin(0 auto map-get($spacers, 5) auto);
    // outline: 1px solid red;

    > div {
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
      align-content: center;
      align-items: center;
      position: absolute;
      top: 13%;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
    }
  }

  &__clip {
    @include rfs(12rem, width);
    height: auto;
  }

  &__stars {
    @include rfs(5rem, width);
    height: auto;
  }

  &__score {
    color: #23AD7A;
    @include font-size(map-get($lp, 'font-size-xl'));
    font-weight: $font-weight-bolder;
  }

  &__trustpilot-logo {
    @include rfs(5rem, width);
    height: auto;
  }

  &__heading {
    --from: #212B36;
    --to: #212B36;
    --shadow: #ffffff01;

    width: 60%;
    @include margin(0 auto (map-get($spacers, 6) * 2) auto);
    text-align: center;
  }

  &__slider {
    position: relative;
    width: 90%;
    margin: 0 auto;
  }

  &__nav {
    position: absolute;
    top: 30%;

    &.--is-left {
      left: -5%;
    }

    &.--is-right {
      right: -5%;
    }

    > button {
      margin: 0;
      padding: 0;
      border: 0;
      outline: none;
      background: none;
      color: $ink-lighter;
      @include font-size(5rem);
      line-height: 1;

      &:active {
        color: $ink-light;
      }
    }
  }

  &__item {
    width: 80%;
    margin: 0 auto;
  }

  &__card {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-content: center;
    height: 100%;
    text-align: center;
    @include padding(map-get($spacers, 6) map-get($spacers, 6));
    border-radius: $border-radius-lg;
    background: linear-gradient(rgba(255, 255, 255, 0.3), $white);
    box-shadow: 10px 10px 34px rgba(0, 0, 0, 0.1);
    z-index: 0;

    &:after {
      content: '';
      display: block;
      position: absolute;
      bottom: -30px;
      left: 20px;
      width: 0px;
      height: 0px;
      border-style: solid;
      border-width: 60px 30px 0 40px;
      border-color: #fff transparent transparent transparent;
      transform: rotate(340deg);
    }
  }

  &__logo {
    @include imageRendering;
    @include rfs(10rem, width);
    height: auto;
    @include margin(0 auto map-get($spacers, 4) auto);
  }

  &__quote {
    margin: 0;
    @include font-size(map-get($lp, 'font-size-lg'));
    font-weight: $font-weight-light;
    font-style: italic;
  }

  &__author {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    @include padding-top(map-get($spacers, 5));
    @include padding-left(map-get($spacers, 6));

    figcaption {
      display: flex;
      flex-direction: column;

      br {
        display: none;
      }
    }
  }

  &__author-avatar {
    @include imageRendering;
    @include rfs(3.5rem, width);
    @include rfs(3.5rem, height);
    border-radius: 50%;
    @include margin-right(map-get($spacers, 3));
  }

  &__author-name {
    font-weight: $font-weight-bold;
    font-style: normal;
  }

  &__author-description {
    color: $ink-lighter;
    @include font-size(map-get($lp, 'font-size-sm'));
  }

  /* Glide */
  .glide {
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-md')) {
    &__heading {
      width: 70%;
    }
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-sm')) {
    &__heading {
      width: 80%;
    }
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-xs')) {
    &__heading {
      width: 90%;
    }

    &__badge > div {
      top: 20%;
    }

    &__trustpilot-logo {
      display: none;
    }
  }
}
</style>
